import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, FoldersIcon, PlusIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  BookingCategory,
  useCreateBookingCategory,
  useUpdateBookingCategory,
} from '@/api/booking-types'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard } from '@/hooks'
import { FormChangeFields, getFormChanges } from '@/pages/organisation/shared/form-changes'
import { PersonFormLayout } from '@/pages/organisation/shared/person-form-layout'
import { getApiErrorMessage } from '@/utils/error'

import { UpdatedAt } from '../../shared/updated-at'
import {
  BOOKING_NAME_MAX_LENGTH,
  GroupFormData,
  getGroupFormDefaults,
  makeGroupFormSchema,
} from './group-form-modal.constants'

interface GroupFormModalProps {
  group?: BookingCategory
  nextOrder: number
  onClose: VoidFunction
  onSaved?: (id: number) => void
}

export const GroupFormModal = ({ group, nextOrder, onClose, onSaved }: GroupFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createGroup } = useCreateBookingCategory()
  const { mutateAsync: updateGroup } = useUpdateBookingCategory()
  const isEdit = !!group

  const schema = useMemo(() => makeGroupFormSchema(t), [t])

  const form = useForm<GroupFormData>({
    defaultValues: getGroupFormDefaults(group),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState

  const changeFields = useMemo<FormChangeFields<GroupFormData>>(
    () => ({ name: { label: t('organisation:bookingTypes.group.fields.name.label') } }),
    [t],
  )
  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<GroupFormData> = async ({ name }) => {
    try {
      if (group) {
        await updateGroup({ id: group.id, name, order: group.order })
        toastFactory.success(t('organisation:bookingTypes.group.toasts.updated'))
      } else {
        const created = await createGroup({ name, order: nextOrder })
        toastFactory.success(t('organisation:bookingTypes.group.toasts.created'))
        onSaved?.(created.id)
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit
          ? t('organisation:bookingTypes.group.titleEdit')
          : t('organisation:bookingTypes.group.titleNew')
      }
      headerIcon={FoldersIcon}
      footer={
        <>
          <UpdatedAt value={group?.updatedAt} />
          <Button use="secondary" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <TextInputFormField
          withReservedFooter
          required
          label={t('organisation:bookingTypes.group.fields.name.label')}
          maxLength={BOOKING_NAME_MAX_LENGTH}
          name="name"
          placeholder={t('organisation:bookingTypes.group.fields.name.placeholder')}
        />
      </PersonFormLayout>
    </Modal>
  )
}
